/**
 * Kargonode Compliance Rules Engine — Phase 1
 * ─────────────────────────────────────────────
 * Centralised list of mandatory documents that a shipment must carry
 * before it can be liquidated. Conditional rules support transport-mode
 * and FOB-threshold predicates.
 *
 * 🔑 Architecture note: this module is intentionally a pure constant.
 * The contract `ComplianceRule[]` is shaped to be servable from the
 * database in a future phase (org_compliance_rules table) without
 * requiring any consumer refactor — the `useComplianceRules` hook
 * already provides the abstraction layer.
 */
import type { MasterDocType } from "./documentTypes";

/** Lightweight context passed to conditional rule predicates. */
export interface ComplianceContext {
  /** Detected transport mode. */
  modoTransporte?: "Marítimo" | "Aéreo" | "Terrestre" | null;
  /** Total FOB value of the shipment in USD (for threshold rules like DAV). */
  fobTotalUSD?: number;
  /** True when the active DIM document has both numero_dim and numero_levante populated. */
  dimDataComplete?: boolean;
}

export interface ComplianceRule {
  /** Stable identifier (used for React keys + audit logs). */
  id: string;
  /** Document type as defined in MASTER_DOC_TYPES. */
  tipoDocumento: MasterDocType;
  /** Display label for the checklist (shorter than tipoDocumento). */
  label: string;
  /**
   * `critical` — blocks liquidation. The Liquidar button is disabled.
   * `warning`  — surfaces in the checklist but does NOT block.
   */
  severity: "critical" | "warning";
  /** Short description shown in the popover for the user. */
  descripcion: string;
  /**
   * Optional predicate. When omitted the rule is always applicable.
   * Returning `false` removes the rule from the active checklist.
   */
  appliesIf?: (ctx: ComplianceContext) => boolean;
}

export const DEFAULT_COMPLIANCE_RULES: ComplianceRule[] = [
  {
    id: "factura",
    tipoDocumento: "Factura Internacional",
    label: "Factura Comercial",
    severity: "critical",
    descripcion: "Documento base para valor en aduana (DIAN).",
  },
  {
    id: "packing-list",
    tipoDocumento: "Packing List",
    label: "Packing List",
    severity: "critical",
    descripcion: "Detalle físico de la mercancía (peso, bultos).",
  },
  {
    id: "bl",
    tipoDocumento: "BL",
    label: "BL (Bill of Lading)",
    severity: "critical",
    descripcion: "Documento de transporte marítimo obligatorio.",
    appliesIf: (ctx) => ctx.modoTransporte === "Marítimo",
  },
  {
    id: "guia-aerea",
    tipoDocumento: "Guía Aérea",
    label: "Guía Aérea (AWB)",
    severity: "critical",
    descripcion: "Documento de transporte aéreo obligatorio.",
    appliesIf: (ctx) => ctx.modoTransporte === "Aéreo",
  },
  {
    id: "dim",
    tipoDocumento: "Declaración de Importación",
    label: "Declaración de Importación (PDF)",
    severity: "critical",
    descripcion: "Archivo de la DIM presentada ante la DIAN. Bloquea liquidación.",
  },
  {
    id: "dim-data",
    // Same doc family as `dim` — but the predicate in useLiquidationGuard
    // checks structured fields (numero_dim + numero_levante) rather than
    // mere PDF presence. Always applicable; toggles present/absent.
    tipoDocumento: "Declaración de Importación",
    label: "Datos de Nacionalización (DIM + Levante)",
    severity: "warning",
    descripcion:
      "Captura el Número de DIM y el Número de Levante desde la pestaña Aduanas para completar el Nexo Comercial DIAN.",
  },
  {
    id: "dav",
    tipoDocumento: "Declaración Andina de Valor",
    label: "Declaración Andina de Valor",
    severity: "warning",
    descripcion: "Obligatoria para importaciones FOB > USD 5.000.",
    appliesIf: (ctx) => (ctx.fobTotalUSD ?? 0) > 5000,
  },
  {
    id: "certificado-origen",
    tipoDocumento: "Certificado de Origen",
    label: "Certificado de Origen",
    severity: "warning",
    descripcion: "Necesario para aplicar preferencias arancelarias (TLC).",
  },
];

/**
 * Filters the rule set by the runtime context (transport mode, FOB).
 * Pure function — easy to unit-test.
 */
export function getApplicableRules(
  rules: ComplianceRule[],
  ctx: ComplianceContext,
): ComplianceRule[] {
  return rules.filter((r) => (r.appliesIf ? r.appliesIf(ctx) : true));
}
